import { Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

interface AnimatedWordRotatorProps {
  words: string[]
  interval?: number
  fontSize?: { xs: string; md: string; lg: string }
  fontWeight?: number
  color?: string
  letterSpacing?: string
}

export default function AnimatedWordRotator({
  words,
  interval = 3000,
  fontSize = { xs: '42px', md: '56px', lg: '68px' },
  fontWeight = 800,
  color = '#6366f1',
  letterSpacing = '-0.03em',
}: AnimatedWordRotatorProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    const timer = setInterval(() => {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % words.length)
        setIsAnimating(false)
      }, 500) // Half of the animation duration (1000ms total)
    }, interval)

    return () => clearInterval(timer)
  }, [words.length, interval])

  return (
    <Box
      sx={{
        display: 'inline-block',
        position: 'relative',
        minWidth: { xs: '180px', md: '220px', lg: '280px' },
        textAlign: 'left',
        verticalAlign: 'baseline',
        height: '1.1em', // Match lineHeight
      }}
    >
      <Typography
        component='span'
        sx={{
          fontSize,
          fontWeight,
          lineHeight: 1.2,
          letterSpacing,
          display: 'inline-block',
          position: 'absolute',
          top: 0,
          left: 0,
          whiteSpace: 'nowrap',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundSize: '200% 200%',
          animation: 'gradient-shift 3s ease infinite',
          opacity: isAnimating ? 0 : 1,
          transform: isAnimating ? 'translateY(-25px) scale(0.95)' : 'translateY(0) scale(1)',
          transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
          filter: isAnimating ? 'blur(4px)' : 'blur(0px)',
          '@keyframes gradient-shift': {
            '0%, 100%': {
              backgroundPosition: '0% 50%',
            },
            '50%': {
              backgroundPosition: '100% 50%',
            },
          },
        }}
      >
        {words[currentIndex]}
      </Typography>
    </Box>
  )
}
